import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectPrivyToken } from '../../../redux/slices/auth';
import { useSolana } from '../../../hooks/useSolana';
import { selectTokenByNetworkAndAddress } from '../../../redux/slices/tokenSlice';
import { useGetGiftQuery } from '../../../redux/api/authorized/gifts';
import {
  useClaimPromotionalGiftMutation,
  useBroadcastClaimMutation,
} from '../../../redux/api/authorized/promotional';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Widget } from '@typeform/embed-react';
import WidthContainer from '../../../components/WidthContainer';
import SendingStatus from '../SendingStatus';
import GiftSkeleton from './ClaimGiftSkeleton';

const ClaimSurveyGift = () => {
  const { id } = useParams();
  const privyToken = useSelector(selectPrivyToken);
  const [claimError, setClaimError] = useState(null);
  const navigate = useNavigate();
  const { signAndSerializeTransaction, walletsReady } = useSolana();
  const [sendingStatus, setSendingStatus] = useState('idle');
  const [hasCompletedSurvey, setHasCompletedSurvey] = useState(false);

  const { data, error, isLoading } = useGetGiftQuery(id, {
    skip: !id || !privyToken,
  });

  const { fromTokenAmount } = useSolana();

  const [claimPromotionalGift] = useClaimPromotionalGiftMutation();
  const [broadcastClaim] = useBroadcastClaimMutation();

  const gift = data?.gift;
  const token = useSelector((state) =>
    selectTokenByNetworkAndAddress(state, gift?.network, gift?.token),
  );

  useEffect(() => {
    if (hasCompletedSurvey && gift?.status !== 'HTLC_CLAIMED') {
      handleClaim();
    }
  }, [hasCompletedSurvey, gift?.status]);

  useEffect(() => {
    if (data?.canClaim) {
      setHasCompletedSurvey(true);
    }
  }, [data?.canClaim, setHasCompletedSurvey]);

  const handleClaim = async () => {
    try {
      setClaimError(null);
      setSendingStatus('sending');

      // First, claim the promotional gift to get the transaction
      const claimResult = await claimPromotionalGift(id).unwrap();

      // If the gift is already claimed, redirect to claim page
      if (
        claimResult.message === 'You have already claimed this gift.' &&
        claimResult.giftId
      ) {
        navigate(`/app/gifts/${claimResult.giftId}/claim`);
        return;
      }

      // Sign the transaction
      let signedTransaction;
      try {
        signedTransaction = await signAndSerializeTransaction(
          claimResult.serializedTransaction,
        );
      } catch (signError) {
        if (signError.code === 4001) {
          toast.info('Transaction was not approved');
          setSendingStatus('error');
          return;
        }
        throw signError;
      }

      await broadcastClaim({
        giftId: id,
        serializedTransaction: signedTransaction,
      }).unwrap();

      setSendingStatus('success');
      toast.success('Gift claimed successfully!');
    } catch (err) {
      const errorMessage =
        err.data?.message ||
        (err.message === 'User rejected the request'
          ? 'Transaction was not approved'
          : 'Failed to claim gift');
      setClaimError(errorMessage);
      setSendingStatus('error');
      toast.error(errorMessage);
    }
  };

  const handleRetry = () => {
    setSendingStatus('idle');
    setClaimError(null);
    handleClaim();
  };

  if (isLoading || !gift) return <GiftSkeleton />;
  if (error) return <p>Error loading gift: {error.message}</p>;
  if (!walletsReady) return <GiftSkeleton />;

  if (
    hasCompletedSurvey &&
    gift.status === 'HTLC_CLAIMED' &&
    sendingStatus !== 'success'
  ) {
    navigate(`/app/gifts/${id}/claim`);
    return null;
  }

  if (sendingStatus !== 'idle') {
    return (
      <SendingStatus
        email={gift.receiver.email}
        token={token}
        cardImage={gift.imageUrl}
        isSuccess={sendingStatus === 'success'}
        isError={sendingStatus === 'error'}
        tokenAmount={fromTokenAmount(gift.baseTokenAmount, token?.decimals)}
        successText='Your gift has been claimed! '
        errorText={claimError || 'Failed to claim gift'}
        successButtonText='See Token'
        errorButtonText='Try Again'
        successAction={() => navigate('/app/home')}
        errorAction={handleRetry}
        loadingText='Claiming gift...'
      />
    );
  }

  return (
    <WidthContainer>
      <Widget
        id='kYlGTLrG'
        onSubmit={() => setHasCompletedSurvey(true)}
        style={{ width: '100%', height: '500px' }}
      />
    </WidthContainer>
  );
};

export default ClaimSurveyGift;
